import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { AddRounded, DeleteOutlineRounded } from '@mui/icons-material'
import SmartSelect from '../../../../generic/smartSelect/SmartSelect'
import { apiGet } from '../../../../generic/Api_Functions'
import SmartMateriaUITable from '../../../../generic/SmartMateriaUITable'

// Caps the resource table's height so an unpaginated list of materials does not push the rest of
// the incidence form off screen.
const RESOURCE_TABLE_CONTAINER_STYLE = { overflow: 'auto', maxHeight: '24rem' }

const normalizeApiList = (response) => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.content)) return response.content
  return []
}

const normalizeJobOption = (item) => {
  const id = item?.uuid ?? item?.id ?? null
  const name = String(item?.name ?? item?.code ?? '').trim()
  if (!id || !name) return null
  return { id, name }
}

// Several jobs can share the same name (different nodes). The job multi-select identifies options by
// their label, so duplicate names would be indistinguishable and selecting one would select them all.
// We make the labels unique by suffixing repeats — "Inspect-1", "Inspect-1 (2)", … — while leaving the
// first occurrence untouched so labels already stored against existing scopes keep matching.
const disambiguateJobOptionNames = (options) => {
  const seenCountByName = new Map()
  return options.map((option) => {
    const occurrence = (seenCountByName.get(option.name) || 0) + 1
    seenCountByName.set(option.name, occurrence)
    return occurrence === 1 ? option : { ...option, name: `${option.name} (${occurrence})` }
  })
}

const toFabricationOrderOption = (item) => ({
  id: item?.id ?? null,
  code: String(item?.fabricationOrder ?? item?.code ?? item?.name ?? '').trim()
})

const buildResourceLabel = (resource) => {
  const code = String(resource?.resourceCode ?? resource?.code ?? '').trim()
  const name = String(resource?.resourceName ?? resource?.name ?? '').trim()
  return [code, name].filter(Boolean).join(' · ')
}

const parseAffectedUnits = (value, fallbackValue = null) => {
  const normalized = String(value || '').trim()
  if (!normalized) return fallbackValue
  const parsed = Number.parseInt(normalized, 10)
  if (Number.isNaN(parsed) || parsed < 1) return fallbackValue
  return parsed
}

const getAffectedUnits = (row, fallbackValue = 1) => (
  parseAffectedUnits(row?.affectedUnits ?? row?.affected, fallbackValue)
)

const sumAffectedUnits = (rows) => {
  const affectedRows = Array.isArray(rows) ? rows : []
  if (affectedRows.length === 0) return ''
  return String(affectedRows.reduce((total, row) => total + getAffectedUnits(row, 1), 0))
}

const getRawMaterialReference = (resource) => (
  resource?.rawMaterialReference ||
  resource?.rawMaterialReferenceDto ||
  null
)

const getScopeResourceId = (resource) => {
  const reference = getRawMaterialReference(resource)
  if (resource?.isInstance) {
    return resource?.resourceId ??
      resource?.id ??
      resource?.rawMaterialReferenceId ??
      resource?.rawMaterialReferenceDtoId ??
      reference?.id ??
      reference?.resourceId ??
      null
  }

  return resource?.rawMaterialReferenceId ??
    resource?.rawMaterialReferenceDtoId ??
    reference?.id ??
    reference?.resourceId ??
    resource?.resourceId ??
    resource?.id ??
    null
}

const getScopeResourceCode = (resource) => {
  const reference = getRawMaterialReference(resource)
  return String(
    resource?.rawMaterialReferenceCode ??
    resource?.resourceReferenceCode ??
    reference?.resourceCode ??
    reference?.code ??
    resource?.resourceCode ??
    resource?.code ??
    ''
  ).trim()
}

const getScopeResourceName = (resource) => {
  const reference = getRawMaterialReference(resource)
  return String(
    resource?.rawMaterialReferenceName ??
    resource?.rawMaterialReferenceDescription ??
    resource?.resourceReferenceName ??
    reference?.resourceName ??
    reference?.name ??
    reference?.description ??
    resource?.resourceName ??
    resource?.name ??
    ''
  ).trim()
}

const buildJobScopedResourceLabel = ({ jobName, resourceCode, resourceName }) => {
  const base = [resourceCode, resourceName].filter(Boolean).join(' - ')
  return jobName ? `${jobName} - ${base}` : base
}

// Each option represents a (job, resource) pair. Two jobs holding the same material yield two
// separate options so the operator can target each job independently.
const toScopeResourceOption = (resource, job) => {
  const resourceId = getScopeResourceId(resource)
  if (resourceId == null) return null
  const resourceCode = getScopeResourceCode(resource)
  const resourceName = getScopeResourceName(resource)
  const jobId = job?.id ?? resource?.jobId ?? null
  const jobName = String(job?.name ?? resource?.jobName ?? '').trim()
  const label = buildJobScopedResourceLabel({ jobName, resourceCode, resourceName }) ||
    String(resource?.label ?? buildResourceLabel(resource)).trim()
  return {
    scopeAffectedRowId: resource?.scopeAffectedRowId,
    rawMaterialReferenceId: resource?.rawMaterialReferenceId,
    resourceId,
    resourceCode,
    resourceName,
    jobId,
    jobName,
    label,
    affected: String(getAffectedUnits(resource, 1)),
    isInstance: Boolean(resource?.isInstance)
  }
}

const uniqueScopeResourceOptions = (taggedResources) => {
  const map = new Map()
  const rows = Array.isArray(taggedResources) ? taggedResources : []
  rows.forEach(({ resource, job }) => {
    const option = toScopeResourceOption(resource, job)
    if (!option) return
    // Dedup is per (jobId, resourceId) so the same material in two jobs stays as two options.
    const jobPart = option.jobId != null ? `j:${option.jobId}` : `jn:${option.jobName || ''}`
    const key = `${jobPart}::${option.scopeAffectedRowId ?? option.resourceId}`
    if (map.has(key)) return
    map.set(key, option)
  })
  return Array.from(map.values())
}

// A scope row carries `jobs` as an array of { id, name, scopeUuid? } so the user can target one or
// many jobs from a single row. `scopeUuid` is the originating backend scope id (when the row was
// loaded from the API and merged from N scopes); it is preserved through edits so that updates do
// not orphan existing rows. On save the row is exploded into one backend scope per job.
const normalizeJobsField = (row) => {
  if (Array.isArray(row?.jobs)) {
    return row.jobs
      .map((entry) => entry && typeof entry === 'object'
        ? {
            id: entry.id ?? null,
            name: String(entry.name ?? '').trim(),
            scopeUuid: entry.scopeUuid ?? null,
            hasActions: Boolean(entry.hasActions)
          }
        : { id: null, name: String(entry ?? '').trim(), scopeUuid: null, hasActions: false })
      .filter((entry) => entry.id != null || entry.name)
  }
  if (row?.job && typeof row.job === 'object') {
    const id = row.job.id ?? null
    const name = String(row.job.name ?? '').trim()
    if (id == null && !name) return []
    return [{ id, name, scopeUuid: null, hasActions: Boolean(row.job.hasActions) }]
  }
  const name = String(row?.job ?? '').trim()
  return name ? [{ id: null, name, scopeUuid: null, hasActions: false }] : []
}

const normalizeScopeRows = (scopeRows) => {
  const rows = Array.isArray(scopeRows) ? scopeRows : []
  return rows
    .map((row) => {
      const normalizedResources = normalizeScopeResourceList(row?.resource)
      const affectedRows = Array.isArray(row?.affectedRows) ? row.affectedRows : []
      const jobs = normalizeJobsField(row)
      return {
        id: String(row?.id ?? row?.scopeRowId ?? row?.uuid ?? `${row?.fabricationOrderCode ?? ''}-${Math.random()}`),
        scopeRowId: String(row?.scopeRowId ?? `${row?.fabricationOrderCode ?? ''}-${Math.random()}`),
        uuid: row?.uuid ?? null,
        affectedRows,
        hasActions: Boolean(row?.hasActions),
        fabricationOrderId: row?.fabricationOrderId ?? null,
        fabricationOrderCode: String(row?.fabricationOrderCode ?? '').trim(),
        jobs,
        resource: normalizedResources,
        affected: normalizeScopeAffectedValue(row?.affected, normalizedResources, affectedRows),
        // Preserve so the parent submit handler can diff originalScopeUuids vs the current
        // jobs' scopeUuid and mark removed job-scopes for backend deletion.
        originalScopeUuids: Array.isArray(row?.originalScopeUuids) ? row.originalScopeUuids : [],
        endDate: row?.endDate ?? null,
        originalAffected: row?.originalAffected ?? null,
        affectedIsTotal: Boolean(row?.affectedIsTotal)
      }
    })
    .filter((row) => row.fabricationOrderCode)
}

const createScopeRow = (fabricationOrder) => ({
  scopeRowId: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  fabricationOrderId: fabricationOrder?.id ?? null,
  fabricationOrderCode: String(fabricationOrder?.code ?? '').trim(),
  jobs: [],
  resource: [],
  affected: ''
})

const normalizeScopeResourceList = (resource) => {
  return Array.isArray(resource)
    ? resource.map((item, index) => {
      const resourceId = item?.resourceId ?? item?.id ?? null
      const jobId = item?.jobId ?? null
      const jobName = String(item?.jobName ?? '').trim()
      const resourceRowId = String(item?.resourceRowId ?? item?.uuid ?? item?.scopeAffectedRowId ?? `${jobId ?? 'nojob'}-${resourceId ?? 'resource'}-${index}`)
      const resourceCode = String(item?.resourceCode ?? item?.code ?? '').trim()
      const resourceName = String(item?.resourceName ?? item?.name ?? '').trim()
      return {
        resourceRowId,
        scopeAffectedRowId: item?.scopeAffectedRowId ?? null,
        rawMaterialReferenceId: item?.rawMaterialReferenceId ?? null,
        resourceId,
        jobId,
        jobName,
        uuid: item?.uuid ?? null,
        hasActions: Boolean(item?.hasActions),
        resourceCode,
        resourceName,
        label: String(item?.label ?? buildJobScopedResourceLabel({ jobName, resourceCode, resourceName }) ?? buildResourceLabel(item)).trim(),
        affected: String(getAffectedUnits(item, 1))
      }
    }).filter((item) => item.resourceId != null || item.label)
    : []
}

const normalizeScopeAffectedValue = (affected, resources, affectedRows = resources) => {
  const normalizedResources = normalizeScopeResourceList(resources)
  if (normalizedResources.length === 0) return ''

  const rawValue = String(affected ?? '').trim()
  const parsed = Number.parseInt(rawValue, 10)
  if (Number.isNaN(parsed) || parsed < 1) return sumAffectedUnits(affectedRows)
  return String(parsed)
}

const mergeExistingScopeResourceData = (resources, previousResources) => {
  const previousByUuid = new Map()
  const previousByResourceId = new Map()
  ;(Array.isArray(previousResources) ? previousResources : []).forEach((resource) => {
    if (resource?.uuid) previousByUuid.set(resource.uuid, resource)
    if (resource?.resourceId != null) previousByResourceId.set(String(resource.resourceId), resource)
  })

  return (Array.isArray(resources) ? resources : []).map((resource) => {
    const previous = (resource?.uuid && previousByUuid.get(resource.uuid)) ||
      (resource?.resourceId != null && previousByResourceId.get(String(resource.resourceId))) ||
      null

    if (!previous) return resource

    return {
      ...resource,
      uuid: resource.uuid ?? previous.uuid ?? null,
      hasActions: Boolean(resource.hasActions || previous.hasActions),
      affected: resource.affected ?? previous.affected
    }
  })
}

const scopeRowHasActions = (row) => (
  Boolean(row?.hasActions) ||
  (Array.isArray(row?.resource) && row.resource.some((resource) => resource?.hasActions)) ||
  (Array.isArray(row?.affectedRows) && row.affectedRows.some((resource) => resource?.hasActions))
)

const canEditScopeResourceRow = (row) => !String(row?.uuid ?? '').trim()
const hasPersistedScopeResourceRow = (row) => Boolean(String(row?.uuid ?? '').trim())

export const ScopeResourcesAccordion = ({
  t_,
  scopeRow,
  scopeRows,
  onScopeRowsChange,
  onScopeRowsDeleteBlocked,
  disabled
}) => {
  const [resourceOptions, setResourceOptions] = useState([])
  const [pendingResources, setPendingResources] = useState([])
  const [isLoadingResources, setIsLoadingResources] = useState(false)
  const resources = useMemo(() => normalizeScopeResourceList(scopeRow?.resource || scopeRow?.affectedRows), [scopeRow])
  // The selection key is (jobId, resourceId) — the same material in two jobs counts as two rows
  // and the operator can add/remove each one independently.
  const buildSelectionKey = (jobId, resourceId) => `${jobId ?? 'nojob'}::${resourceId ?? 'nores'}`
  const selectedResourceKeys = useMemo(() => (
    new Set(resources.map((resource) => buildSelectionKey(resource.jobId, resource.resourceId)))
  ), [resources])
  const resourceOptionsByLabel = useMemo(() => {
    const map = new Map()
    resourceOptions.forEach((resource) => {
      if (resource.label) map.set(resource.label, resource)
    })
    return map
  }, [resourceOptions])
  const selectableResourceLabels = useMemo(() => (
    resourceOptions
      .filter((resource) => !selectedResourceKeys.has(buildSelectionKey(resource.jobId, resource.resourceId)))
      .map((resource) => resource.label)
      .filter(Boolean)
  ), [resourceOptions, selectedResourceKeys])

  const scopeJobs = useMemo(() => Array.isArray(scopeRow?.jobs) ? scopeRow.jobs : [], [scopeRow?.jobs])
  const scopeJobsKey = useMemo(() => scopeJobs.map((entry) => entry?.id ?? '').join('|'), [scopeJobs])

  useEffect(() => {
    let cancelled = false

    const loadResources = async () => {
      const fabricationOrderId = scopeRow?.fabricationOrderId
      if (!fabricationOrderId) {
        setResourceOptions([])
        return
      }

      // Job is mandatory on the scope row, so without a selected job there are no resources to
      // surface — bail out instead of fetching the OF-wide list (that would mix in materials
      // from other jobs the user did not pick).
      const jobIds = scopeJobs.map((entry) => entry?.id).filter(Boolean)
      if (jobIds.length === 0) {
        setResourceOptions([])
        return
      }

      setIsLoadingResources(true)
      try {
        // Resource candidates come strictly from the selected jobs' workflows. We fan out a fetch
        // per job and tag each material with its owning job, so the operator sees one entry per
        // (job, material) — job1 with no materials simply contributes nothing, no leakage from
        // job2.
        const jobsToFetch = scopeJobs.filter((entry) => entry?.id != null)
        const responses = await Promise.all(jobsToFetch.map((entry) => (
          apiGet(`partWorkflowHistory/rawMaterial/byFoAndWorkflow/${fabricationOrderId}`, { nodeId: entry.id })
        )))
        if (cancelled) return
        const tagged = responses.flatMap((response, idx) => {
          const job = jobsToFetch[idx]
          return normalizeApiList(response).map((resource) => ({ resource, job }))
        })
        setResourceOptions(uniqueScopeResourceOptions(tagged).filter((item) => item.label))
      } catch (_) {
        if (cancelled) return
        setResourceOptions([])
      } finally {
        if (!cancelled) setIsLoadingResources(false)
      }
    }

    loadResources()

    return () => {
      cancelled = true
    }
  }, [scopeRow?.fabricationOrderId, scopeJobsKey, scopeJobs])

  const updateScopeResources = useCallback((nextResources) => {
    const normalizedResources = normalizeScopeResourceList(nextResources)
    const nextRows = scopeRows.map((row) => {
      if (row.scopeRowId !== scopeRow.scopeRowId) return row
      return {
        ...row,
        resource: normalizedResources,
        affectedRows: normalizedResources,
        affected: sumAffectedUnits(normalizedResources)
      }
    })
    onScopeRowsChange(nextRows)
  }, [onScopeRowsChange, scopeRow?.scopeRowId, scopeRows])

  const addResources = useCallback(() => {
    const labels = Array.isArray(pendingResources) ? pendingResources : []
    if (labels.length === 0) return

    const nextResources = [...resources]
    labels.forEach((label) => {
      const option = resourceOptionsByLabel.get(label)
      if (!option) return
      if (selectedResourceKeys.has(buildSelectionKey(option.jobId, option.resourceId))) return
      nextResources.push({
        ...option,
        resourceRowId: `${option.jobId ?? 'nojob'}-${option.resourceId ?? ''}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        uuid: null,
        hasActions: false,
        affected: '1'
      })
    })

    updateScopeResources(nextResources)
    setPendingResources([])
  }, [pendingResources, resourceOptionsByLabel, resources, selectedResourceKeys, updateScopeResources])

  const resourceColumns = useMemo(() => ([
    {
      name: t_('Job'),
      field: 'jobName',
      readOnly: true
    },
    {
      name: t_('Code'),
      field: 'resourceCode',
      readOnly: true
    },
    {
      name: t_('Name'),
      field: 'resourceName',
      readOnly: true
    },
    {
      name: t_('AFFECTED'),
      field: 'affected',
      readOnly: (row) => !canEditScopeResourceRow(row)
    }
  ]), [t_])

  const deleteScopeResources = useCallback((items) => {
    const rowsToDelete = Array.isArray(items) ? items : []
    if (rowsToDelete.some((row) => row?.hasActions)) {
      onScopeRowsDeleteBlocked?.()
      return false
    }

    const idsToDelete = new Set(rowsToDelete.map((row) => row.resourceRowId))
    updateScopeResources(resources.filter((resource) => !idsToDelete.has(resource.resourceRowId)))
    return rowsToDelete.length > 0
  }, [onScopeRowsDeleteBlocked, resources, updateScopeResources])

  const resourceActions = useMemo(() => ({
    custom: [
      {
        name: 'Delete',
        hide: (row) => !hasPersistedScopeResourceRow(row),
        disabled: (row) => row?.hasActions,
        onClick: (row) => deleteScopeResources([row]),
        render: (row) => {
          if (!hasPersistedScopeResourceRow(row)) return null
          const deleteDisabled = Boolean(row?.hasActions)
          return (
            <Tooltip title={deleteDisabled ? t_('Can not delete because it has actions') : t_('Delete')}>
              <span>
                <IconButton
                  size='small'
                  disabled={deleteDisabled}
                >
                  <DeleteOutlineRounded />
                </IconButton>
              </span>
            </Tooltip>
          )
        }
      }
    ],
    edit: {
      visibilityButton: canEditScopeResourceRow,
      fetchFunction: async (row) => {
        const affected = String(getAffectedUnits(row, 1))
        const nextResources = resources.map((resource) =>
          resource.resourceRowId === row.resourceRowId
            ? { ...resource, affected }
            : resource
        )
        updateScopeResources(nextResources)
        return { ...row, affected }
      }
    },
    delete: {
      visibilityButton: (row) => !hasPersistedScopeResourceRow(row),
      disabledButton: (row) => row?.hasActions,
      fetchFunction: async (items) => {
        return deleteScopeResources(items)
      }
    }
  }), [deleteScopeResources, resources, t_, updateScopeResources])

  return (
    <Box sx={{ p: 1.5 }}>
      <Grid container spacing={2} alignItems='flex-end'>
        <Grid item xs={12} sm={8}>
          <SmartSelect
            multiple
            label={t_('resources')}
            value={pendingResources}
            selectableOptions={selectableResourceLabels}
            onChange={setPendingResources}
            disabled={disabled || isLoadingResources || !scopeRow?.fabricationOrderId}
            helperText={isLoadingResources ? t_('Loading') : selectableResourceLabels.length === 0 ? t_('No materials available') : ''}
            disableClearable={false}
          />
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
          <Button
            variant='outlined'
            color='primary'
            sx={{ borderColor: 'transparent', minHeight: 56 }}
            onClick={addResources}
            disabled={disabled || isLoadingResources || pendingResources.length === 0}
            fullWidth
          >
            <AddRounded />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
              {t_('Add').toUpperCase()}
            </Typography>
          </Button>
        </Grid>
        <Grid item xs={12}>
          {resources.length === 0
            ? (
              <Typography variant='body2' color='text.secondary'>
                {t_('No materials added')}
              </Typography>
              )
            : (
              // The pagination footer is hidden here, so the default page size of 10 would
              // silently hide every material past the tenth with no control to reach it.
              // Render them all and let the container scroll instead.
              <SmartMateriaUITable
                columns={resourceColumns}
                dataFetch={resources}
                actions={disabled ? {} : resourceActions}
                primaryKeys={['resourceRowId']}
                toolbarDisplay='none'
                searchAndPaginationVisible='none'
                tablePageSize={Number.MAX_SAFE_INTEGER}
                tableContainerStyle={RESOURCE_TABLE_CONTAINER_STYLE}
                dense
              />
              )}
        </Grid>
      </Grid>
    </Box>
  )
}

const ScopesSection = ({
  t_,
  scopeRows = [],
  onScopeRowsChange,
  onScopeRowsDelete,
  onScopeRowsDeleteBlocked,
  disabled
}) => {
  const showAllFabricationOrders = false
  const [fabricationOrders, setFabricationOrders] = useState([])
  const [pendingFabricationOrders, setPendingFabricationOrders] = useState([])
  const [isLoadingFabricationOrders, setIsLoadingFabricationOrders] = useState(false)
  const [editingScopeRow, setEditingScopeRow] = useState(null)
  const [jobOptionsByFabricationOrderId, setJobOptionsByFabricationOrderId] = useState({})
  const editingScopeRowRef = useRef(editingScopeRow)
  // Snapshot of the jobs array at edit-start time. The table's multi-select widget mutates the
  // live row in place before invoking onChange, so this is the only reliable place to look up
  // the pre-edit state (jobs with hasActions, scopeUuid…) when deciding what was dropped.
  const editingScopeJobsBaselineRef = useRef([])
  // Full snapshot of the row at edit-start. We propagate job/resource changes to the parent
  // state on every multi-select tweak (so the form's Save sees them without an inline commit);
  // if the operator cancels the inline edit instead, this snapshot lets us restore the row.
  const editingRowSnapshotRef = useRef(null)
  const editCommittedRef = useRef(false)
  const jobOptionsByFabricationOrderIdRef = useRef(jobOptionsByFabricationOrderId)

  useEffect(() => {
    editingScopeRowRef.current = editingScopeRow
  }, [editingScopeRow])

  useEffect(() => {
    jobOptionsByFabricationOrderIdRef.current = jobOptionsByFabricationOrderId
  }, [jobOptionsByFabricationOrderId])

  useEffect(() => {
    let cancelled = false

    const loadFabricationOrders = async () => {
      setIsLoadingFabricationOrders(true)
      try {
        const response = await apiGet('allFabricationOrderSimple', { all: showAllFabricationOrders })
        if (cancelled) return
        const nextOrders = normalizeApiList(response)
          .map(toFabricationOrderOption)
          .filter((item) => item.code)
        setFabricationOrders(nextOrders)
      } catch (_) {
        if (cancelled) return
        setFabricationOrders([])
      } finally {
        if (!cancelled) {
          setIsLoadingFabricationOrders(false)
        }
      }
    }

    loadFabricationOrders()

    return () => {
      cancelled = true
    }
  }, [showAllFabricationOrders])

  const rows = useMemo(() => normalizeScopeRows(scopeRows), [scopeRows])
  const usedFabricationOrderCodes = useMemo(() => (
    new Set(rows.map((row) => row.fabricationOrderCode).filter(Boolean))
  ), [rows])
  const fabricationOrderOptions = useMemo(() => {
    return fabricationOrders
      .map((item) => item.code)
      .filter((code) => code && !usedFabricationOrderCodes.has(code))
  }, [fabricationOrders, usedFabricationOrderCodes])
  const fabricationOrdersByCode = useMemo(() => {
    const map = new Map()
    fabricationOrders.forEach((item) => {
      if (!item.code || map.has(item.code)) return
      map.set(item.code, item)
    })
    return map
  }, [fabricationOrders])

  const helperText = isLoadingFabricationOrders
    ? t_('Loading')
    : fabricationOrderOptions.length === 0
      ? t_('No fabrication orders available')
      : ''

  const addScopeRows = useCallback(() => {
    const selectedCodes = Array.isArray(pendingFabricationOrders) ? pendingFabricationOrders : []
    if (selectedCodes.length === 0) return

    const nextRows = [...rows]
    selectedCodes.forEach((code) => {
      if (!code || usedFabricationOrderCodes.has(code)) return
      nextRows.push(createScopeRow(fabricationOrdersByCode.get(code) || { id: null, code }))
    })
    onScopeRowsChange(nextRows)
    setPendingFabricationOrders([])
  }, [fabricationOrdersByCode, onScopeRowsChange, pendingFabricationOrders, rows, usedFabricationOrderCodes])

  useEffect(() => {
    let cancelled = false

    const loadJobOptions = async () => {
      const fabricationOrderId = editingScopeRow?.fabricationOrderId
      if (!fabricationOrderId || jobOptionsByFabricationOrderIdRef.current[fabricationOrderId]) return

      try {
        const workflow = await apiGet(`fabricationOrderJobs/${fabricationOrderId}`)
        if (cancelled) return
        const sourceItems = Array.isArray(workflow?.nodes) ? workflow.nodes : normalizeApiList(workflow)
        const nextOptions = disambiguateJobOptionNames(
          sourceItems
            .filter((item) => Array.isArray(workflow?.nodes)
              ? String(item?.nodeType ?? '').toUpperCase() === 'JOB' || item?.jobReference
              : true)
            .map(normalizeJobOption)
            .filter(Boolean)
        )

        setJobOptionsByFabricationOrderId((prev) => ({
          ...prev,
          [fabricationOrderId]: nextOptions
        }))
      } catch (_) {
        if (cancelled) return
        setJobOptionsByFabricationOrderId((prev) => ({
          ...prev,
          [editingScopeRow.fabricationOrderId]: []
        }))
      }
    }

    loadJobOptions()

    return () => {
      cancelled = true
    }
  }, [editingScopeRow?.fabricationOrderId])

  const loadJobColumnOptions = useCallback(async () => {
    const currentEditingRow = editingScopeRowRef.current
    const fabricationOrderId = currentEditingRow?.fabricationOrderId
    if (!fabricationOrderId) return []
    return jobOptionsByFabricationOrderId[fabricationOrderId] || []
  }, [jobOptionsByFabricationOrderId])

  const handleJobColumnChange = useCallback((editingRow) => {
    const fabricationOrderId = editingRow?.fabricationOrderId
    const options = jobOptionsByFabricationOrderIdRef.current[fabricationOrderId] || []
    const optionsByName = new Map(options.map((opt) => [opt.name, opt]))
    // The multi-select widget rewrites the jobs array in place with plain { id, name } objects
    // *before* this onChange fires, so the row reference's `jobs` is already the new selection.
    // We pull the pre-edit list from the snapshot captured at edit-start to re-attach the
    // originating scopeUuid and hasActions, and to detect which jobs the user dropped.
    const previousJobs = Array.isArray(editingScopeJobsBaselineRef.current) ? editingScopeJobsBaselineRef.current : []
    const previousJobsById = new Map()
    const previousJobsByName = new Map()
    previousJobs.forEach((prev) => {
      if (prev?.id != null) previousJobsById.set(String(prev.id), prev)
      if (prev?.name) previousJobsByName.set(String(prev.name), prev)
    })
    const previousResources = Array.isArray(editingScopeRowRef.current?.resource) ? editingScopeRowRef.current.resource : []
    const incomingJobs = Array.isArray(editingRow?.jobs) ? editingRow.jobs : []

    const incomingNames = new Set(
      incomingJobs.map((entry) => String(entry?.name ?? entry ?? '').trim()).filter(Boolean)
    )

    // A job is locked when the backend already has actions tied to it (either against the scope
    // itself or against one of its resources). Trying to drop a locked job would orphan those
    // actions, so we restore it and let the operator know.
    const lockedDroppedJobs = previousJobs.filter((prev) => {
      if (!prev?.name || incomingNames.has(String(prev.name))) return false
      if (prev?.hasActions) return true
      return previousResources.some((resource) => (
        resource?.hasActions && prev?.id != null && String(resource?.jobId) === String(prev.id)
      ))
    })

    if (lockedDroppedJobs.length > 0) {
      onScopeRowsDeleteBlocked?.()
      lockedDroppedJobs.forEach((job) => {
        if (!incomingNames.has(String(job.name))) {
          incomingJobs.push({ id: job.id, name: job.name })
          incomingNames.add(String(job.name))
        }
      })
    }

    editingRow.jobs = incomingJobs
      .map((entry) => {
        const name = String(entry?.name ?? entry ?? '').trim()
        if (!name) return null
        const matched = optionsByName.get(name)
        const id = matched ? matched.id : (entry?.id ?? null)
        const previousById = id != null ? previousJobsById.get(String(id)) : null
        const previousByName = previousJobsByName.get(name)
        const previous = previousById || previousByName
        const scopeUuid = entry?.scopeUuid ?? previous?.scopeUuid ?? null
        const hasActions = Boolean(previous?.hasActions)
        return matched
          ? { id: matched.id, name: matched.name, scopeUuid, hasActions }
          : { id, name, scopeUuid, hasActions }
      })
      .filter(Boolean)

    // Drop only the resources tied to jobs the user removed. Resources that belong to jobs still
    // present in the row stay so the operator does not lose work when adding/removing a job.
    const remainingJobIds = new Set(editingRow.jobs.map((job) => String(job?.id)).filter((value) => value !== 'null' && value !== ''))
    editingRow.resource = previousResources.filter((resource) => (
      resource?.jobId == null || remainingJobIds.has(String(resource.jobId))
    ))
    editingRow.affected = sumAffectedUnits(editingRow.resource)
    setEditingScopeRow({ ...editingRow })

    // Propagate the change to the parent's scopeRows state right away. The inline edit's "save"
    // button is the only thing that normally calls onScopeRowsChange, so without this the
    // form's Save handler still sees the pre-edit jobs and the orphan-detect (originalScopeUuids
    // vs current jobs) cannot mark removed jobs for deletion on the backend.
    const nextRows = rows.map((row) => (
      row.scopeRowId === editingRow.scopeRowId ? { ...row, ...editingRow } : row
    ))
    onScopeRowsChange(nextRows)
  }, [onScopeRowsChange, onScopeRowsDeleteBlocked, rows])

  const columns = useMemo(() => ([
    {
      name: t_('OF'),
      field: 'fabricationOrderCode',
      readOnly: true
    },
    {
      name: t_('JOBS'),
      field: 'jobs.name',
      type: 'smartMultiSelect',
      edit: {
        source: loadJobColumnOptions,
        id: 'id',
        field: 'name'
      },
      mandatory: true,
      onChange: handleJobColumnChange
    }
    /* {
      name: t_('Total affecteds'),
      field: 'affected',
      readOnly: true
    } */
  ]), [t_, handleJobColumnChange, loadJobColumnOptions])

  const actions = useMemo(() => ({
    custom: [],
    edit: {
      fetchFunction: async (row) => {
        // Flag this as a real commit so the cancel-restore path in onEditIndexChanged knows
        // it should NOT revert the parent state from the snapshot.
        editCommittedRef.current = true
        const currentRow = rows.find((currentRow) => currentRow.scopeRowId === row.scopeRowId)
        const normalizedResource = mergeExistingScopeResourceData(
          normalizeScopeResourceList(currentRow?.resource),
          currentRow?.resource || currentRow?.affectedRows
        )
        const normalizedRow = {
          ...row,
          jobs: normalizeJobsField(row),
          resource: normalizedResource,
          affectedRows: normalizedResource,
          affected: sumAffectedUnits(normalizedResource)
        }

        const nextRows = rows.map((currentRow) =>
          currentRow.scopeRowId === row.scopeRowId
            ? {
                ...currentRow,
                ...normalizedRow
              }
            : currentRow
        )
        onScopeRowsChange(nextRows)
        return normalizedRow
      }
    },
    delete: {
      disabledButton: scopeRowHasActions,
      fetchFunction: async (items) => {
        const rowsToDelete = Array.isArray(items) ? items : []
        if (rowsToDelete.some(scopeRowHasActions)) {
          onScopeRowsDeleteBlocked?.()
          return false
        }

        const idsToDelete = new Set(rowsToDelete.map((item) => item.scopeRowId))
        onScopeRowsDelete?.(rowsToDelete)
        onScopeRowsChange(rows.filter((row) => !idsToDelete.has(row.scopeRowId)))
        return rowsToDelete.length > 0
      }
    },
    accordion: {
      tooltip: () => t_('resources'),
      render: (row) => (
        <ScopeResourcesAccordion
          t_={t_}
          scopeRow={row}
          scopeRows={rows}
          onScopeRowsChange={onScopeRowsChange}
          onScopeRowsDeleteBlocked={onScopeRowsDeleteBlocked}
          disabled={disabled}
        />
      )
    }
  }), [disabled, onScopeRowsChange, onScopeRowsDelete, onScopeRowsDeleteBlocked, rows, t_])

  const readOnlyActions = useMemo(() => ({
    accordion: actions.accordion,
    custom: []
  }), [actions.accordion])

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant='subtitle1' sx={{ fontWeight: 800, mb: 1 }}>
        {t_('Scopes')}
      </Typography>
      <Grid container spacing={2} alignItems='flex-end'>
        <Grid item xs={12} sm={8}>
          <SmartSelect
            multiple
            label={t_('Affected fabrication order (OF)')}
            value={pendingFabricationOrders}
            selectableOptions={fabricationOrderOptions}
            onChange={setPendingFabricationOrders}
            disabled={disabled || isLoadingFabricationOrders}
            helperText={helperText}
            disableClearable={false}
          />
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
          <Button
            variant='outlined'
            color='primary'
            sx={{
              borderColor: 'transparent',
              minHeight: 56
            }}
            onClick={addScopeRows}
            disabled={disabled || isLoadingFabricationOrders || pendingFabricationOrders.length === 0}
            fullWidth
          >
            <AddRounded />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
              {t_('Add').toUpperCase()}
            </Typography>
          </Button>
        </Grid>

        <Grid item xs={12}>
          {isLoadingFabricationOrders && (
            <Stack direction='row' alignItems='center' gap={1} sx={{ mb: 1 }}>
              <CircularProgress size={18} />
              <Typography variant='body2' color='text.secondary'>
                {t_('Loading fabrication orders')}
              </Typography>
            </Stack>
          )}
          {rows.length === 0
            ? (
              <Typography variant='body2' color='text.secondary'>
                {t_('No scopes added')}
              </Typography>
              )
            : (
              <SmartMateriaUITable
                columns={columns}
                dataFetch={rows}
                actions={disabled ? readOnlyActions : actions}
                primaryKeys={['scopeRowId']}
                toolbarDisplay='none'
                dense
                onEditIndexChanged={(editIndex, row) => {
                  if (editIndex > -2 && row) {
                    // Snapshot jobs by value, not by reference: the table mutates row.jobs in
                    // place when the multi-select fires onChange, so we keep the pre-edit list
                    // here to know exactly what the operator had before they touched anything.
                    editingScopeJobsBaselineRef.current = Array.isArray(row?.jobs)
                      ? row.jobs.map((job) => ({ ...job }))
                      : []
                    // Full row snapshot so we can restore the parent state if the operator
                    // cancels: handleJobColumnChange propagates every chip toggle to the
                    // parent so the form's Save sees the live state without an inline commit,
                    // which means cancel would otherwise leave those mid-edit changes stuck.
                    editingRowSnapshotRef.current = {
                      ...row,
                      jobs: Array.isArray(row?.jobs) ? row.jobs.map((j) => ({ ...j })) : [],
                      resource: Array.isArray(row?.resource) ? row.resource.map((r) => ({ ...r })) : []
                    }
                    editCommittedRef.current = false
                    setEditingScopeRow(row)
                    return
                  }
                  // Edit ended. fetchFunction (commit) flips editCommittedRef on. If we get
                  // here with the flag still false, the user pressed cancel — restore the
                  // pre-edit jobs/resource we propagated mid-edit.
                  if (!editCommittedRef.current && editingRowSnapshotRef.current) {
                    const snapshot = editingRowSnapshotRef.current
                    const restoredRows = rows.map((currentRow) => (
                      currentRow.scopeRowId === snapshot.scopeRowId
                        ? { ...currentRow, jobs: snapshot.jobs, resource: snapshot.resource, affectedRows: snapshot.resource, affected: sumAffectedUnits(snapshot.resource) }
                        : currentRow
                    ))
                    onScopeRowsChange(restoredRows)
                  }
                  editingScopeJobsBaselineRef.current = []
                  editingRowSnapshotRef.current = null
                  editCommittedRef.current = false
                  setEditingScopeRow(null)
                }}
              />
              )}
        </Grid>
      </Grid>
    </Box>
  )
}

ScopesSection.propTypes = {
  t_: PropTypes.func.isRequired,
  format: PropTypes.shape({ dateTime: PropTypes.string }).isRequired,
  scopeRows: PropTypes.arrayOf(PropTypes.shape({
    scopeRowId: PropTypes.string,
    uuid: PropTypes.string,
    hasActions: PropTypes.bool,
    fabricationOrderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fabricationOrderCode: PropTypes.string,
    jobs: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    })),
    resource: PropTypes.arrayOf(PropTypes.shape({
      scopeAffectedRowId: PropTypes.string,
      rawMaterialReferenceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      resourceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      resourceCode: PropTypes.string,
      resourceName: PropTypes.string,
      label: PropTypes.string,
      affected: PropTypes.string,
      isInstance: PropTypes.bool
    })),
    affectedRows: PropTypes.array,
    affected: PropTypes.string
  })),
  onScopeRowsChange: PropTypes.func.isRequired,
  onScopeRowsDelete: PropTypes.func,
  onScopeRowsDeleteBlocked: PropTypes.func,
  endDate: PropTypes.instanceOf(Date),
  onEndDateChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  title: PropTypes.string
}

export default React.memo(ScopesSection)
